import { AfterViewChecked, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import { SpinnerComponent } from 'src/app/modules/shared/spinner/spinner.component';
import { LifestyleService } from 'src/app/services/ha-new/lifestyle.service';
import { LookupService } from 'src/app/services/ha-new/lookup.service';
import { NewHAService } from 'src/app/services/ha-new/new-ha.service';

@Component({
	selector: 'app-work-life-balance',
	templateUrl: './work-life-balance.component.html',
	styleUrls: ['./work-life-balance.component.scss'],
})
export class WorkLifeBalanceComponent implements OnInit, AfterViewChecked {
	/** Data carrier subscriptions */
	subscriptions: Subscription[] = [];
	/** holds ha id */
	healthAssessmentID: number = 0;
	/** holds appointment id */
	apptID: any;
	/** holds readOnly flag */
	readOnly: boolean = false;
	/** holds work life balance data from service */
	workLifeData: any;
	/** holds scroll variable */
	scrollTo = '';
	/** holds form group */
	workLifeForm: FormGroup;
	hoursSpentWorkingMaxValue: number = 40;
	daysAwayFromHomeMaxValue: number = 31;
	workingHoursLookup: any = [];
	nightsAwayFromHomeLookup: any = [];
	workingHoursSlider = {
		step: 1,
		showTickMarks: true,
		min: 0,
		max: 0,
	};
	nightsAwayFromHomeSlider = {
		step: 1,
		showTickMarks: true,
		min: 0,
		max: 0,
	};
	returningUser: boolean = false;
	hoursMissedFromWorkLookup: any = [];
	healthProblemAbilityAffectLookup: any = [];

	constructor(
		private lifestyleService: LifestyleService,
		private spinner: SpinnerComponent,
		private activatedRoute: ActivatedRoute,
		private haService: NewHAService,
		private lookupService: LookupService
	) {
		this.activatedRoute.queryParams.subscribe(params => {
			//console.log('params:', params);
			this.apptID = params.apptID;
			this.readOnly = params.readOnly === 'true';
			this.healthAssessmentID = params.healthAssessmentID;
			this.returningUser = params.returningUser === 'true';
			if (this.scrollTo != 'false') {
				this.scrollTo = params.section;
			}
		});
	}

	ngOnInit() {
		this.initializeWorkLifeForm();
		this.getLookupOptions();
	}

	getLookupOptions() {
		this.spinner.startLoader();
		forkJoin({
			getWorkingHoursLookup: this.lookupService.getWorkingHoursOptions(),
			getNightsAwayFromHomeLookup: this.lookupService.getNightsAwayFromHomeLookup(),
			getHoursMissedFromWorkLookup: this.lookupService.getHoursMissedFromWorkLookup(),
			getHealthProblemAbilityAffectLookup: this.lookupService.getHealthProblemAbilityAffectLookup(),
			getWorklife: this.lifestyleService.getWorkLifeDetails(this.apptID, this.healthAssessmentID),
		}).subscribe(
			res => {
				this.spinner.stopLoader();

				this.workingHoursLookup = res.getWorkingHoursLookup.data;
				this.workingHoursSlider['max'] = this.workingHoursLookup.length;
				this.workingHoursSlider['min'] = 1;

				this.nightsAwayFromHomeLookup = res.getNightsAwayFromHomeLookup.data;
				this.nightsAwayFromHomeSlider['max'] = this.nightsAwayFromHomeLookup.length;
				this.nightsAwayFromHomeSlider['min'] = 1;

				this.hoursMissedFromWorkLookup = res.getHoursMissedFromWorkLookup.data;
				this.healthProblemAbilityAffectLookup = res.getHealthProblemAbilityAffectLookup.data;
				this.getWorkLifeData(res.getWorklife.data);

				if (this.scrollTo != 'false') {
					if (this.haService.scrollView(this.scrollTo)) {
						this.scrollTo = 'false';
					}
				}
			},
			err => {
				console.error(`[${WorkLifeBalanceComponent.name}][${this.getLookupOptions.name}]`, 'error getting how you think data', err);
				this.spinner.stopLoader();
			}
		);
	}

	/**
	 * after view checked - initializing scroll
	 */
	ngAfterViewChecked() {
		if (this.scrollTo != 'false') {
			if (this.haService.scrollView(this.scrollTo)) {
				this.scrollTo = 'false';
			}
		}
	}

	initializeWorkLifeForm() {
		this.workLifeForm = new FormGroup({
			hoursSpentWorking: new FormControl(0),
			spendTimeWithFamily: new FormControl(''),
			nightsAwayFromHome: new FormControl(0),
			numberOfChildren: new FormControl(''),
			numberOfChildrenLivingWithYou: new FormControl(''),
			hoursMissedFromWorkHealthProblems: new FormControl(''),
			hoursMissedFromWorkOtherReason: new FormControl(''),
			healthProblemsAffectProductivity: new FormControl(''),
			healthProblemsAffectAbility: new FormControl(''),
		});
	}

	getWorkLifeData(response) {
		//console.log(response);
		this.workLifeData = response;
		this.workLifeForm.controls.hoursSpentWorking.setValue(response.hoursSpentWorking);
		this.workLifeForm.controls.spendTimeWithFamily.setValue(response.spendTimeWithFamily);
		this.workLifeForm.controls.nightsAwayFromHome.setValue(response.nightsAwayFromHome);
		this.workLifeForm.controls.numberOfChildren.setValue(response.numberOfChildren);
		this.workLifeForm.controls.numberOfChildrenLivingWithYou.setValue(response.numberOfChildrenLivingWithYou);
		this.workLifeForm.controls.hoursMissedFromWorkHealthProblems.setValue(response.hoursMissedFromWorkHealthProblems);
		this.workLifeForm.controls.hoursMissedFromWorkOtherReason.setValue(response.hoursMissedFromWorkOtherReason);
		this.workLifeForm.controls.healthProblemsAffectProductivity.setValue(response.healthProblemsAffectProductivity);
		this.workLifeForm.controls.healthProblemsAffectAbility.setValue(response.healthProblemsAffectAbility);
	}

	saveWorkLifeData() {
		if (!this.readOnly) {
			const formData = this.workLifeForm.getRawValue();
			// Prepopulated value if available and the value has not been updated
			if (this.returningUser || (!this.returningUser && this.selectionMade('hoursSpentWorking'))) {
				// formData.hoursSpentWorking = formData.hoursSpentWorking;
			} else if (this.workLifeData.hoursSpentWorking) {
				formData.hoursSpentWorking = this.workLifeData.hoursSpentWorking;
			} else {
				formData.hoursSpentWorking = undefined;
			}
			if (this.returningUser || (!this.returningUser && this.selectionMade('nightsAwayFromHome'))) {
			} else if (this.workLifeData.nightsAwayFromHome) {
				formData.nightsAwayFromHome = this.workLifeData.nightsAwayFromHome;
			} else {
				formData.nightsAwayFromHome = undefined;
			}
			this.subscriptions.push(
				this.lifestyleService
					.saveWorkLifeDetails(this.apptID, {
						...formData,
						healthAssessmentID: this.healthAssessmentID,
					})
					.subscribe(
						response => {
							//console.log(response);
							this.haService.redirect(
								[],
								[],
								['how-you-sleep'],
								this.apptID,
								this.healthAssessmentID,
								this.readOnly,
								'lifestyle-how-you-sleep',
								this.returningUser
							);
						},
						err => {
							console.error(`[${WorkLifeBalanceComponent.name}][${this.saveWorkLifeData.name}]`, 'An error occurred while saving work life balance data => ', err);
						}
					)
			);
		}
	}

	getHoursSpentWorking(index) {
		if (this.workingHoursLookup[index - 1]) {
			return this.workingHoursLookup[index - 1].name;
		} else if (this.workingHoursLookup && this.workingHoursLookup[0]) {
			return this.workingHoursLookup[0].name;
		}
	}

	getNightsAwayFromHome(index) {
		if (this.nightsAwayFromHomeLookup[index - 1]) {
			return this.nightsAwayFromHomeLookup[index - 1].name;
		} else if (this.nightsAwayFromHomeLookup && this.nightsAwayFromHomeLookup[0]) {
			return this.nightsAwayFromHomeLookup[0].name;
		}
	}

	selectionMade(slider) {
		return this.workLifeForm.get(slider).touched;
	}
}
